import React, { useState, useEffect, useRef } from 'react'
import {
  Timer,
  List,
  Plus,
  Trash2,
  RotateCcw,
  Settings,
  HelpCircle,
  FileText,
  Upload,
  CheckCircle,
  AlertCircle,
  Loader2,
  ShieldAlert,
  Network,
  Projector,
  Shuffle,
  Terminal,
  Youtube
} from 'lucide-react'
import type { LessonActivity } from '@/types'
import { apiClient } from '@/services/api'
import { QuestionBankPicker } from '@/components/lessonBuilder/QuestionBankPicker'
import type { QuestionBankItem } from '@/components/lessonBuilder/QuestionBankPicker'
import { getActivityTypeLabel, getAccentClasses } from '@/constants/activityTypes'
import { questionBankItemToActivityData, questionBankItemToActivityAttrs } from '@/utils/questionBankUtils'
import { resolvePhishingAnswer } from '@/utils/phishing'
import { ALL_CARD_TYPES, ACTION_TYPES, CONTROL_TYPES, getCardDef } from '@/components/potatoPirates/cardDefs'
import type { Card, CardType, ControlType } from '@/components/potatoPirates/engine'
import { CONTROL_PARAMS } from '@/components/potatoPirates/engine'
import { ModelAnswerEditor } from '@/components/potatoPirates/ModelAnswerEditor'


interface ActivityEditorProps {
  activity: LessonActivity | undefined
  onUpdateActivity: (activityId: string, updates: Partial<LessonActivity>) => void
  onActivityRefresh?: (activityId: string, updated: LessonActivity) => void
  onBulkAddQuestions?: (questions: QuestionBankItem[]) => Promise<void>
  onReplaceWithQuestion?: (attrs: { activity_type: string; title: string; instruction: string; activity_data: any; time_limit_seconds: number | null }) => Promise<void>
  loading: boolean
  lessonId?: string
  readOnly?: boolean
}

export function ActivityEditor({ activity, onUpdateActivity, onActivityRefresh, onBulkAddQuestions, onReplaceWithQuestion, loading, lessonId, readOnly = false }: ActivityEditorProps) {
  const [localActivity, setLocalActivity] = useState<LessonActivity | undefined>(activity)
  const [hasChanges, setHasChanges] = useState(false)
  const [pdfUploading, setPdfUploading] = useState(false)
  const [pdfStatus, setPdfStatus] = useState<string | null>(null)
  const [imageUploading, setImageUploading] = useState(false)
  const [explanationImageUploading, setExplanationImageUploading] = useState(false)
  const pollRef = useRef<NodeJS.Timeout | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const explanationImageInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setLocalActivity(activity)
    setHasChanges(false)
    setPdfStatus(null)
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [activity?.id])

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  // Auto-save after changes with debounce
  useEffect(() => {
    if (!hasChanges || !localActivity) return

    if (readOnly) return
    const timeoutId = setTimeout(() => {
      handleSave()
    }, 1000) // Save after 1 second of no changes

    return () => clearTimeout(timeoutId)
  }, [hasChanges, localActivity, readOnly])

  if (!activity) {
    return (
      <div className="flex items-center justify-center h-full bg-[var(--color-surface)]">
        <div className="text-center">
          <div className="text-[var(--color-muted)] mb-3">
            <Settings className="w-12 h-12 mx-auto" />
          </div>
          <p className="text-[var(--color-text-muted)] font-regular mb-2">No activity selected</p>
          <p className="text-sm text-[var(--color-text-muted)] font-light">Select an activity to edit its settings</p>
        </div>
      </div>
    )
  }

  const ac = getAccentClasses(localActivity?.activity_type || '')

  const handleFieldChange = (field: string, value: any) => {
    if (!localActivity || readOnly) return

    const updated = { ...localActivity, [field]: value }
    setLocalActivity(updated)
    setHasChanges(true)
  }

  const handleActivityDataChange = (field: string, value: any) => {
    handleActivityDataChanges({ [field]: value })
  }

  /**
   * Update several activity_data fields at once. Uses a functional state
   * update so multiple fields set together (e.g. correct_answer + type +
   * options) aren't clobbered by React batching stale-closure reads.
   */
  const handleActivityDataChanges = (fields: Record<string, any>) => {
    if (!localActivity || readOnly) return

    setLocalActivity(prev => prev ? {
      ...prev,
      activity_data: {
        ...prev.activity_data,
        ...fields
      }
    } : prev)
    setHasChanges(true)
  }

  /** Load a question bank item into the current activity or replace it */
  const loadFromQuestionBank = (q: QuestionBankItem) => {
    if (onReplaceWithQuestion) {
      onReplaceWithQuestion(questionBankItemToActivityAttrs(q))
      return
    }
    // Update the current activity's data field by field
    const newData = questionBankItemToActivityData(q)
    const updated = {
      ...localActivity,
      activity_data: newData
    }
    setLocalActivity(updated as LessonActivity)
    setHasChanges(true)
  }

  const handleSave = () => {
    if (!localActivity || !hasChanges) return

    onUpdateActivity(localActivity.id, {
      title: localActivity.title,
      instruction: localActivity.instruction,
      activity_data: localActivity.activity_data,
      time_limit_seconds: localActivity.time_limit_seconds
    })
    setHasChanges(false)
  }

  const handleReset = () => {
    setLocalActivity(activity)
    setHasChanges(false)
  }

  const renderInstructionField = () => (
    <div>
      <label className="block text-sm font-medium text-[var(--color-text)] mb-2">
        <FileText className="w-4 h-4 inline mr-1" />
        Instruction (optional)
      </label>
      <textarea
        value={localActivity?.instruction || ''}
        onChange={(e) => handleFieldChange('instruction', e.target.value)}
        readOnly={readOnly}
        rows={2}
        placeholder="Instructions shown to students before they start this activity…"
        className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] text-sm resize-none"
      />
    </div>
  )

  const renderTimeLimitSetting = () => (
    <div>
      <label className="block text-sm font-medium text-[var(--color-text)] mb-2">
        <Timer className="w-4 h-4 inline mr-1" />
        Response Time (seconds)
      </label>
      <input
        type="number"
        value={localActivity?.time_limit_seconds || 300}
        onChange={(e) => handleFieldChange('time_limit_seconds', parseInt(e.target.value) || 300)}
        readOnly={readOnly}
        min="60"
        max="3600"
        step="30"
        className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
      />
      <p className="text-xs text-[var(--color-text-muted)] mt-1">
        Time for participants to choose an answer: {Math.floor((localActivity?.time_limit_seconds || 300) / 60)}m {((localActivity?.time_limit_seconds || 300) % 60)}s
      </p>
    </div>
  )

  const renderHintsSection = () => {
    const hints: string[] = (localActivity?.activity_data as any)?.hints || []

    const addHint = () => {
      handleActivityDataChange('hints', [...hints, ''])
    }

    const updateHint = (idx: number, value: string) => {
      handleActivityDataChange('hints', hints.map((h, i) => i === idx ? value : h))
    }

    const removeHint = (idx: number) => {
      handleActivityDataChange('hints', hints.filter((_, i) => i !== idx))
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-[var(--color-text)]">Hints</label>
          {!readOnly && (
            <button
              type="button"
              onClick={addHint}
              className="text-xs text-[var(--color-primary)] hover:opacity-80 font-medium"
            >
              + Add Hint
            </button>
          )}
        </div>
        <div className="space-y-2">
          {hints.map((h, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input
                type="text"
                value={h}
                onChange={(e) => updateHint(idx, e.target.value)}
                readOnly={readOnly}
                placeholder={`Hint ${idx + 1}`}
                className="flex-1 px-3 py-1.5 text-sm border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              />
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => removeHint(idx)}
                  className="text-xs text-red-500 hover:text-red-500 shrink-0"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          {hints.length === 0 && (
            <p className="text-xs text-[var(--color-text-muted)]">No hints. Students will work without guidance.</p>
          )}
        </div>
      </div>
    )
  }

  const handleExplanationImageUpload = async (file: File) => {
    if (!activity || !lessonId) return
    setExplanationImageUploading(true)
    try {
      const res = await apiClient.uploadExplanationImage(lessonId, activity.id, file)
      const updated = res.data
      if (updated?.activity_data) {
        setLocalActivity(prev => prev ? { ...prev, activity_data: { ...prev.activity_data, explanation_image_url: updated.activity_data.explanation_image_url } } : prev)
        onActivityRefresh?.(activity.id, updated as LessonActivity)
      }
    } catch { /* upload failed */ } finally {
      setExplanationImageUploading(false)
      if (explanationImageInputRef.current) explanationImageInputRef.current.value = ''
    }
  }

  const handleExplanationImageRemove = async () => {
    if (!activity || !lessonId) return
    setExplanationImageUploading(true)
    try {
      const res = await apiClient.deleteExplanationImage(lessonId, activity.id)
      const updated = res.data
      setLocalActivity(prev => prev ? { ...prev, activity_data: { ...prev.activity_data, explanation_image_url: undefined } } : prev)
      if (updated) onActivityRefresh?.(activity.id, updated as LessonActivity)
    } catch { /* remove failed */ } finally {
      setExplanationImageUploading(false)
    }
  }

  const renderExplanationImageUpload = (data: any) => (
    <div>
      <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
        Explanation Image (optional)
      </label>
      {data.explanation_image_url ? (
        <div className="relative inline-block">
          <img src={data.explanation_image_url} alt="" className="max-h-48 rounded-lg object-contain border border-[var(--color-border)]" />
          <button
            onClick={handleExplanationImageRemove}
            disabled={explanationImageUploading}
            className="absolute top-1 right-1 p-1 bg-[var(--color-danger)] text-white rounded-full hover:opacity-80 transition-colors"
          >
            {explanationImageUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
          </button>
        </div>
      ) : (
        <div>
          <input
            ref={explanationImageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handleExplanationImageUpload(file)
            }}
          />
          <button
            onClick={() => explanationImageInputRef.current?.click()}
            disabled={explanationImageUploading || readOnly}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-dashed border-[var(--color-border)] rounded-lg hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors text-[var(--color-text-muted)]"
          >
            {explanationImageUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {explanationImageUploading ? 'Uploading...' : 'Add Image'}
          </button>
        </div>
      )}
    </div>
  )

  const renderMCQEditor = () => {
    const data = localActivity?.activity_data as any || {}
    const rawOptions = data.options || []
    const options = [...rawOptions, '', '', '', ''].slice(0, 4) as string[]

    const correctAnswers: number[] = Array.isArray(data.correct_answers)
      ? data.correct_answers
      : (typeof data.correct_answer === 'number' ? [data.correct_answer] : [])

    // Explicit multi-select flag; legacy questions (no flag) default to
    // multi only when more than one answer was marked correct.
    const multiSelect: boolean = typeof data.multi_select === 'boolean'
      ? data.multi_select
      : correctAnswers.length > 1

    const setMultiSelect = (value: boolean) => {
      if (!localActivity || readOnly) return
      const nextData: any = { ...localActivity.activity_data, multi_select: value }
      // When switching to single-answer, keep only the first correct answer.
      if (!value && correctAnswers.length > 1) {
        nextData.correct_answers = correctAnswers.slice(0, 1)
      }
      setLocalActivity({ ...localActivity, activity_data: nextData })
      setHasChanges(true)
    }

    const toggleCorrectAnswer = (index: number) => {
      // Single-answer mode behaves like radio buttons — picking one replaces any prior choice.
      if (!multiSelect) {
        handleActivityDataChange('correct_answers', correctAnswers.includes(index) ? [] : [index])
        return
      }
      const next = correctAnswers.includes(index)
        ? correctAnswers.filter(i => i !== index)
        : [...correctAnswers, index].sort((a, b) => a - b)
      handleActivityDataChange('correct_answers', next)
    }

    const updateOption = (index: number, value: string) => {
      const newOptions = [...options]
      newOptions[index] = value
      handleActivityDataChange('options', newOptions)
    }

    const handleImageUpload = async (file: File) => {
      if (!activity || !lessonId) return
      setImageUploading(true)
      try {
        const res = await apiClient.uploadActivityImage(lessonId, activity.id, file)
        const updated = res.data
        if (updated?.activity_data) {
          setLocalActivity(prev => prev ? { ...prev, activity_data: { ...prev.activity_data, question_image_url: updated.activity_data.question_image_url } } : prev)
          onActivityRefresh?.(activity.id, updated as LessonActivity)
        }
      } catch { /* upload failed */ } finally {
        setImageUploading(false)
        if (imageInputRef.current) imageInputRef.current.value = ''
      }
    }

    const handleImageRemove = async () => {
      if (!activity || !lessonId) return
      setImageUploading(true)
      try {
        const res = await apiClient.deleteActivityImage(lessonId, activity.id)
        const updated = res.data
        setLocalActivity(prev => prev ? { ...prev, activity_data: { ...prev.activity_data, question_image_url: undefined } } : prev)
        if (updated) onActivityRefresh?.(activity.id, updated as LessonActivity)
      } catch { /* remove failed */ } finally {
        setImageUploading(false)
      }
    }

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Question
          </label>
          <textarea
            value={data.question || ''}
            onChange={(e) => handleActivityDataChange('question', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none font-regular"
            placeholder="Enter your question here"
          />
        </div>

        {/* Question Image */}
        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Question Image (optional)
          </label>
          {data.question_image_url ? (
            <div className="relative inline-block">
              <img src={data.question_image_url} alt="" className="max-h-48 rounded-lg object-contain border border-[var(--color-border)]" />
              <button
                onClick={handleImageRemove}
                disabled={imageUploading}
                className="absolute top-1 right-1 p-1 bg-[var(--color-danger)] text-white rounded-full hover:opacity-80 transition-colors"
              >
                {imageUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              </button>
            </div>
          ) : (
            <div>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleImageUpload(file)
                }}
              />
              <button
                onClick={() => imageInputRef.current?.click()}
                disabled={imageUploading || readOnly}
                className="flex items-center gap-2 px-3 py-2 text-sm border border-dashed border-[var(--color-border)] rounded-lg hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors text-[var(--color-text-muted)]"
              >
                {imageUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {imageUploading ? 'Uploading...' : 'Add Image'}
              </button>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
            <label className="text-sm font-semibold text-[var(--color-text)]">
              Answer Options
              <span className="ml-2 text-xs font-normal text-[var(--color-text-muted)]">
                {multiSelect ? '(check all correct answers)' : '(select the one correct answer)'}
              </span>
            </label>

            {/* Single- vs multi-answer toggle */}
            <div className="inline-flex rounded-lg border border-[var(--color-border)] overflow-hidden text-xs font-semibold shrink-0">
              <button
                type="button"
                onClick={() => setMultiSelect(false)}
                disabled={readOnly}
                className={`px-3 py-1.5 transition-colors ${
                  !multiSelect
                    ? `${ac.bgSolid} text-white`
                    : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                }`}
              >
                Single answer
              </button>
              <button
                type="button"
                onClick={() => setMultiSelect(true)}
                disabled={readOnly}
                className={`px-3 py-1.5 transition-colors ${
                  multiSelect
                    ? `${ac.bgSolid} text-white`
                    : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                }`}
              >
                Multi-select
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {options.map((option: string, index: number) => {
              const isChecked = correctAnswers.includes(index)
              return (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type={multiSelect ? 'checkbox' : 'radio'}
                    name={multiSelect ? undefined : `mcq_correct_${activity?.id ?? 'new'}`}
                    checked={isChecked}
                    onChange={() => toggleCorrectAnswer(index)}
                    className={`w-4 h-4 text-[var(--color-primary)] ${multiSelect ? 'rounded' : ''}`}
                  />
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    isChecked
                      ? `${ac.bgSolid} text-white`
                      : 'bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]'
                  }`}>
                    {'ABCD'[index]}
                  </span>
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] font-regular"
                    placeholder={`Option ${'ABCD'[index]}`}
                  />
                </div>
              )
            })}
          </div>

        </div>

        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Explanation (optional)
          </label>
          <textarea
            value={data.explanation || ''}
            onChange={(e) => handleActivityDataChange('explanation', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none font-regular"
            placeholder="Explain why this answer is correct (shown after answering)"
          />
        </div>

        {renderExplanationImageUpload(data)}

        {renderHintsSection()}
        {renderInstructionField()}
        {renderTimeLimitSetting()}
      </div>
    )
  }

  const renderCodingEditor = () => {
    const data = localActivity?.activity_data as any || {}
    const testCases: { id: string; description: string; expected_output: string }[] = data.test_cases || []
    const hints: string[] = data.hints || []
    const allowedCategories: string[] = data.allowedCategories || ['Logic', 'Loops', 'Math', 'Text', 'Variables', 'Functions']

    const ALL_CATEGORIES = ['Logic', 'Loops', 'Math', 'Text', 'Variables', 'Functions', 'Lists']

    const toggleCategory = (cat: string) => {
      const next = allowedCategories.includes(cat)
        ? allowedCategories.filter((c) => c !== cat)
        : [...allowedCategories, cat]
      handleActivityDataChange('allowedCategories', next)
    }

    const addTestCase = () => {
      const next = [
        ...testCases,
        { id: `test_${Date.now()}`, description: '', expected_output: '' },
      ]
      handleActivityDataChange('test_cases', next)
    }

    const updateTestCase = (idx: number, field: string, value: string) => {
      const next = testCases.map((tc, i) => i === idx ? { ...tc, [field]: value } : tc)
      handleActivityDataChange('test_cases', next)
    }

    const removeTestCase = (idx: number) => {
      handleActivityDataChange('test_cases', testCases.filter((_, i) => i !== idx))
    }

    const addHint = () => {
      handleActivityDataChange('hints', [...hints, ''])
    }

    const updateHint = (idx: number, value: string) => {
      handleActivityDataChange('hints', hints.map((h, i) => i === idx ? value : h))
    }

    const removeHint = (idx: number) => {
      handleActivityDataChange('hints', hints.filter((_, i) => i !== idx))
    }

    return (
      <div className="space-y-6">
        {/* Question Bank */}
        {!readOnly && <QuestionBankPicker activityType="coding" onSelect={loadFromQuestionBank} onBulkAdd={onBulkAddQuestions} />}

        {/* Question */}
        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Question
          </label>
          <textarea
            value={data.question || ''}
            onChange={(e) => handleActivityDataChange('question', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none font-regular"
            placeholder='e.g. Write a program that prints "Hello, World!"'
          />
        </div>

        {/* Test Cases */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-[var(--color-text)]">Test Cases</label>
            <button
              type="button"
              onClick={addTestCase}
              className={`text-xs ${ac.text} ${ac.textHover} font-medium`}
            >
              + Add Test Case
            </button>
          </div>
          {testCases.length === 0 && (
            <p className="text-xs text-[var(--color-text-muted)]">No test cases yet. Add one to give students automatic feedback.</p>
          )}
          <div className="space-y-3">
            {testCases.map((tc, idx) => (
              <div key={tc.id} className="border border-[var(--color-border)] rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-[var(--color-text-muted)]">Test {idx + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeTestCase(idx)}
                    className="text-xs text-red-500 hover:text-red-500"
                  >
                    Remove
                  </button>
                </div>
                <input
                  type="text"
                  value={tc.description}
                  onChange={(e) => updateTestCase(idx, 'description', e.target.value)}
                  placeholder="Description (e.g. Basic test)"
                  className="w-full px-2 py-1.5 text-sm border border-[var(--color-border)] rounded focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                />
                <input
                  type="text"
                  value={tc.expected_output}
                  onChange={(e) => updateTestCase(idx, 'expected_output', e.target.value)}
                  placeholder="Expected output (e.g. Hello, World!)"
                  className="w-full px-2 py-1.5 text-sm border border-[var(--color-border)] rounded font-mono focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Block Categories */}
        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Available Block Categories
          </label>
          <div className="flex flex-wrap gap-2">
            {ALL_CATEGORIES.map((cat) => {
              const active = allowedCategories.includes(cat)
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggleCategory(cat)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? `${ac.bgSubtle} ${ac.borderSubtle} ${ac.text}`
                      : 'bg-[var(--color-surface-deep)] border-[var(--color-border)] text-[var(--color-text-muted)]'
                  }`}
                >
                  {cat}
                </button>
              )
            })}
          </div>
        </div>

        {/* Hints */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-[var(--color-text)]">Hints</label>
            <button
              type="button"
              onClick={addHint}
              className={`text-xs ${ac.text} ${ac.textHover} font-medium`}
            >
              + Add Hint
            </button>
          </div>
          <div className="space-y-2">
            {hints.map((h, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input
                  type="text"
                  value={h}
                  onChange={(e) => updateHint(idx, e.target.value)}
                  placeholder={`Hint ${idx + 1}`}
                  className="flex-1 px-3 py-1.5 text-sm border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                />
                <button
                  type="button"
                  onClick={() => removeHint(idx)}
                  className="text-xs text-red-500 hover:text-red-500 shrink-0"
                >
                  ×
                </button>
              </div>
            ))}
            {hints.length === 0 && (
              <p className="text-xs text-[var(--color-text-muted)]">No hints. Students will work without guidance.</p>
            )}
          </div>
        </div>

        {renderInstructionField()}
        {renderTimeLimitSetting()}
      </div>
    )
  }

  const renderPotatoPiratesEditor = () => {
    const data = (localActivity?.activity_data as any) || {}
    const playerShips: number[] =
      data.player_ships && data.player_ships.length ? data.player_ships : [10, 10]
    const opponentShips: number[] =
      data.opponent_ships && data.opponent_ships.length ? data.opponent_ships : [10, 10]
    const allowedCards: string[] = data.allowedCards || [...ALL_CARD_TYPES]
    const hints: string[] = data.hints || []

    const setPlayerShip = (idx: number, value: number) => {
      const next = [...playerShips]
      next[idx] = Math.max(1, Math.min(10, value || 1))
      handleActivityDataChange('player_ships', next)
    }
    const setPlayerShipCount = (count: number) => {
      if (!localActivity || readOnly) return
      const nextShips = Array.from({ length: count }, (_, i) => playerShips[i] ?? 10)
      const currentAnswer: Card[][] = data.model_answer && Array.isArray(data.model_answer) ? data.model_answer : []
      const resizedAnswer = Array.from({ length: count }, (_, i) => currentAnswer[i] ?? [])
      // Both fields must be written in a single setLocalActivity call — two consecutive
      // handleActivityDataChange calls would both read the same stale localActivity and
      // the second would silently overwrite the first's changes.
      setLocalActivity({
        ...localActivity,
        activity_data: {
          ...localActivity.activity_data,
          player_ships: nextShips,
          model_answer: resizedAnswer,
        },
      })
      setHasChanges(true)
    }
    const setOpponentShip = (idx: number, value: number) => {
      const next = [...opponentShips]
      next[idx] = Math.max(1, Math.min(10, value || 1))
      handleActivityDataChange('opponent_ships', next)
    }
    const setOpponentShipCount = (count: number) => {
      const next = Array.from({ length: count }, (_, i) => opponentShips[i] ?? 10)
      handleActivityDataChange('opponent_ships', next)
    }
    const toggleCard = (t: CardType) => {
      const next = allowedCards.includes(t) ? allowedCards.filter((c) => c !== t) : [...allowedCards, t]
      handleActivityDataChange('allowedCards', next)
    }
    // Toggle a specific param variant of a control card (e.g. 'for:2').
    // If the old plain-type key ('for') is present, expand all param keys first
    // so that disabling one param doesn't silently disable the others.
    const toggleParamCard = (t: ControlType, param: number) => {
      const paramKey = `${t}:${param}`
      let next = [...allowedCards]
      if (next.includes(t)) {
        const expanded = CONTROL_PARAMS[t]
          .filter(p => p !== param)
          .map(p => `${t}:${p}`)
        next = next.filter(c => c !== t).concat(expanded)
      } else if (next.includes(paramKey)) {
        next = next.filter(c => c !== paramKey)
      } else {
        next = [...next, paramKey]
      }
      handleActivityDataChange('allowedCards', next)
    }
    const modelAnswer: Card[][] = data.model_answer && Array.isArray(data.model_answer) ? data.model_answer : Array.from({ length: playerShips.length }, () => [])

    const addHint = () => handleActivityDataChange('hints', [...hints, ''])
    const updateHint = (idx: number, value: string) =>
      handleActivityDataChange('hints', hints.map((h, i) => (i === idx ? value : h)))
    const removeHint = (idx: number) =>
      handleActivityDataChange('hints', hints.filter((_, i) => i !== idx))

    return (
      <div className="space-y-6">
        {/* Question */}
        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">Question</label>
          <textarea
            value={data.question || ''}
            onChange={(e) => handleActivityDataChange('question', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none font-regular"
            placeholder="e.g. Destroy the enemy fleet in one run. Use a control card to attack efficiently!"
          />
        </div>

        {/* Objective */}
        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Objective / hint text <span className="font-normal text-[var(--color-text-muted)]">(optional)</span>
          </label>
          <textarea
            value={data.objective || ''}
            onChange={(e) => handleActivityDataChange('objective', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none font-regular"
            placeholder="e.g. The enemy has two ships — plan your three card slots per ship."
          />
        </div>

        {/* Player fleet */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-[var(--color-text)]">Player Fleet</label>
            <div className="flex gap-1">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPlayerShipCount(n)}
                  className={`px-2 py-0.5 rounded text-xs font-medium border transition-colors ${
                    playerShips.length === n
                      ? `${ac.bgSubtle} ${ac.borderSubtle} ${ac.text}`
                      : 'bg-[var(--color-surface-deep)] border-[var(--color-border)] text-[var(--color-text-muted)]'
                  }`}
                >
                  {n} ship{n === 1 ? '' : 's'}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {playerShips.map((potatoes, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="text-xs font-medium text-[var(--color-text-muted)] w-16">Ship {idx + 1}</span>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={potatoes}
                  onChange={(e) => setPlayerShip(idx, Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-semibold text-[var(--color-text)] w-12 text-right">{potatoes} 🥔</span>
              </div>
            ))}
          </div>
        </div>

        {/* Opponent fleet */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-[var(--color-text)]">Enemy Fleet</label>
            <div className="flex gap-1">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setOpponentShipCount(n)}
                  className={`px-2 py-0.5 rounded text-xs font-medium border transition-colors ${
                    opponentShips.length === n
                      ? `${ac.bgSubtle} ${ac.borderSubtle} ${ac.text}`
                      : 'bg-[var(--color-surface-deep)] border-[var(--color-border)] text-[var(--color-text-muted)]'
                  }`}
                >
                  {n} ship{n === 1 ? '' : 's'}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {opponentShips.map((potatoes, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="text-xs font-medium text-[var(--color-text-muted)] w-16">Ship {idx + 1}</span>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={potatoes}
                  onChange={(e) => setOpponentShip(idx, Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-semibold text-[var(--color-text)] w-12 text-right">{potatoes} 🥔</span>
              </div>
            ))}
          </div>
        </div>

        {/* Allowed cards */}
        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">Available Cards</label>
          <div className="flex flex-wrap gap-2">
            {/* Action cards — single toggle per type */}
            {ACTION_TYPES.map((t) => {
              const active = allowedCards.includes(t)
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleCard(t)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? `${ac.bgSubtle} ${ac.borderSubtle} ${ac.text}`
                      : 'bg-[var(--color-surface-deep)] border-[var(--color-border)] text-[var(--color-text-muted)]'
                  }`}
                >
                  {getCardDef(t).label}
                </button>
              )
            })}
            {/* Control cards — one toggle per param value */}
            {CONTROL_TYPES.flatMap((t) =>
              CONTROL_PARAMS[t].map((param) => {
                const paramKey = `${t}:${param}`
                const active = allowedCards.includes(paramKey) || allowedCards.includes(t)
                const label = t === 'for'
                  ? `For ${param} times`
                  : t === 'while'
                    ? `While > ${param} 🥔`
                    : `If ≤ ${param} 🥔`
                return (
                  <button
                    key={paramKey}
                    type="button"
                    onClick={() => toggleParamCard(t, param)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? `${ac.bgSubtle} ${ac.borderSubtle} ${ac.text}`
                        : 'bg-[var(--color-surface-deep)] border-[var(--color-border)] text-[var(--color-text-muted)]'
                    }`}
                  >
                    {label}
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* Model Answer */}
        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-1">Model Answer</label>
          <p className="text-xs text-[var(--color-text-muted)] mb-3">
            Set up the intended correct card program. Students see this after running their battle.
          </p>
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-3">
            <ModelAnswerEditor
              key={playerShips.length}
              programs={modelAnswer}
              shipCount={playerShips.length}
              allowedCards={allowedCards}
              activityMode
              onChange={(programs) => handleActivityDataChange('model_answer', programs)}
            />
          </div>
        </div>

        {/* Hints */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-[var(--color-text)]">Hints</label>
            <button type="button" onClick={addHint} className={`text-xs ${ac.text} ${ac.textHover} font-medium`}>
              + Add Hint
            </button>
          </div>
          <div className="space-y-2">
            {hints.map((h, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input
                  type="text"
                  value={h}
                  onChange={(e) => updateHint(idx, e.target.value)}
                  placeholder={`Hint ${idx + 1}`}
                  className="flex-1 px-3 py-1.5 text-sm border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                />
                <button
                  type="button"
                  onClick={() => removeHint(idx)}
                  className="text-xs text-red-500 hover:text-red-500 shrink-0"
                >
                  ×
                </button>
              </div>
            ))}
            {hints.length === 0 && (
              <p className="text-xs text-[var(--color-text-muted)]">No hints. Students will work without guidance.</p>
            )}
          </div>
        </div>

        {renderInstructionField()}
        {renderTimeLimitSetting()}
      </div>
    )
  }

  const startPollingStatus = () => {
    if (pollRef.current) clearInterval(pollRef.current)
    if (!activity || !lessonId) return

    pollRef.current = setInterval(async () => {
      try {
        const res = await apiClient.request<any>('get', `/lessons/${lessonId}/activities/${activity.id}`)
        const fetched = res.data?.data
        const status = fetched?.activity_data?.processing_status
        setPdfStatus(status)
        if (fetched?.activity_data) {
          setLocalActivity(prev => {
            const updated = prev ? { ...prev, activity_data: fetched.activity_data } : prev
            // Push the refreshed activity to the parent so the preview panel updates
            if (updated && onActivityRefresh) {
              onActivityRefresh(updated.id, updated)
            }
            return updated
          })
        }
        if (status === 'ready' || status === 'failed') {
          clearInterval(pollRef.current!)
          pollRef.current = null
        }
      } catch {
        // ignore polling errors
      }
    }, 2000)
  }

  const handlePdfSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !activity || !lessonId) return

    setPdfUploading(true)
    setPdfStatus('uploading')

    try {
      await apiClient.uploadActivityPdf(lessonId, activity.id, file)
      setPdfStatus('processing')
      startPollingStatus()
    } catch {
      setPdfStatus('failed')
    } finally {
      setPdfUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const renderPdfSlideshowEditor = () => {
    const data = localActivity?.activity_data as any || {}
    const status: string = pdfStatus ?? data.processing_status ?? 'pending'

    const statusLabel = () => {
      switch (status) {
        case 'uploading': return 'Uploading…'
        case 'processing': return 'Processing pages…'
        case 'ready': return `Ready (${data.total_slides} slide${data.total_slides === 1 ? '' : 's'})`
        case 'failed': return 'Processing failed. Try re-uploading.'
        default: return 'No PDF uploaded yet'
      }
    }

    const StatusIcon = () => {
      if (status === 'ready') return <CheckCircle className="w-4 h-4 text-green-600" />
      if (status === 'failed') return <AlertCircle className="w-4 h-4 text-red-500" />
      if (status === 'uploading' || status === 'processing') return <Loader2 className="w-4 h-4 text-[var(--color-secondary)] animate-spin" />
      return <FileText className="w-4 h-4 text-[var(--color-muted)]" />
    }

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            <FileText className="w-4 h-4 inline mr-1" />
            PDF Slideshow
          </label>
          <p className="text-xs text-[var(--color-text-muted)] mb-3">
            Upload a PDF. Each page will be rendered as an image slide that players see in real time.
            The original PDF is never accessible to players.
          </p>

          <div className="flex items-center gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={pdfUploading || !lessonId}
              className="ds-btn ds-btn-primary ds-btn-sm"
            >
              <Upload className="w-4 h-4" />
              {status === 'ready' ? 'Replace PDF' : 'Upload PDF'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handlePdfSelect}
            />
          </div>

          <div className="mt-3 flex items-center gap-2 text-sm">
            <StatusIcon />
            <span className={
              status === 'ready' ? 'text-green-700' :
              status === 'failed' ? 'text-red-500' :
              'text-[var(--color-text-muted)]'
            }>
              {statusLabel()}
            </span>
          </div>
        </div>

        {renderInstructionField()}
      </div>
    )
  }

  const renderGoogleSlidesEditor = () => {
    const data = localActivity?.activity_data as any || {}
    const url: string = data.google_slides_url || ''

    const extractPresentationId = (raw: string): string | null => {
      const match = raw.match(/presentation\/d\/([a-zA-Z0-9_-]+)/)
      return match ? match[1] : null
    }

    const presentationId = extractPresentationId(url)
    const isValid = !!presentationId
    const embedUrl = presentationId
      ? `https://docs.google.com/presentation/d/${presentationId}/embed?start=false&loop=false&delayms=3000`
      : ''

    const handleUrlChange = (raw: string) => {
      const id = extractPresentationId(raw)
      const embed = id
        ? `https://docs.google.com/presentation/d/${id}/embed?start=false&loop=false&delayms=3000`
        : ''
      // Update all three fields together
      if (!localActivity) return
      const updated = {
        ...localActivity,
        activity_data: {
          ...localActivity.activity_data,
          google_slides_url: raw,
          presentation_id: id || '',
          embed_url: embed
        }
      }
      setLocalActivity(updated)
      setHasChanges(true)
    }

    return (
      <div className="space-y-6">
        {!readOnly && (
          <div>
            <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
              <Projector className="w-4 h-4 inline mr-1" />
              Google Slides URL
            </label>
            <p className="text-xs text-[var(--color-text-muted)] mb-3">
              Paste a Google Slides URL. The presentation must be shared as "Anyone with the link can view".
            </p>

            <input
              type="text"
              value={url}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://docs.google.com/presentation/d/…/edit"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm font-regular ${
                url.length === 0
                  ? 'border-[var(--color-border)] focus:ring-[var(--color-primary)]'
                  : isValid
                  ? 'border-green-400 focus:ring-green-500'
                  : 'border-red-400 focus:ring-red-500'
              }`}
            />

            {url.length > 0 && (
              <div className={`mt-2 flex items-center gap-2 text-sm ${isValid ? 'text-green-700' : 'text-red-500'}`}>
                {isValid ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Valid Google Slides URL detected</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span>Invalid URL — must be a Google Slides presentation link</span>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {renderInstructionField()}

        {/* Live preview */}
        {isValid && embedUrl && (
          <div>
            <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">Preview</label>
            <div
              className="relative w-full rounded-lg overflow-hidden border border-[var(--color-border)] pb-[56.25%]"
              onContextMenu={(e) => e.preventDefault()}
            >
              <iframe
                src={embedUrl}
                className="absolute inset-0 w-full h-full"
                sandbox="allow-scripts allow-same-origin"
                allowFullScreen
                loading="lazy"
                title="Google Slides Preview"
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderYouTubeVideoEditor = () => {
    const data = localActivity?.activity_data as any || {}
    const url: string = data.youtube_url || ''

    const extractVideoId = (raw: string): string | null => {
      const match = raw.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
      return match ? match[1] : null
    }

    const videoId = extractVideoId(url)
    const isValid = !!videoId
    const embedUrl = videoId
      ? `https://www.youtube-nocookie.com/embed/${videoId}?controls=0&modestbranding=1&rel=0&disablekb=1&iv_load_policy=3&playsinline=1`
      : ''

    const handleUrlChange = (raw: string) => {
      const id = extractVideoId(raw)
      const embed = id
        ? `https://www.youtube-nocookie.com/embed/${id}?controls=0&modestbranding=1&rel=0&disablekb=1&iv_load_policy=3&playsinline=1`
        : ''
      if (!localActivity) return
      const updated = {
        ...localActivity,
        activity_data: {
          ...localActivity.activity_data,
          youtube_url: raw,
          video_id: id || '',
          embed_url: embed
        }
      }
      setLocalActivity(updated)
      setHasChanges(true)
    }

    return (
      <div className="space-y-6">
        {!readOnly && (
          <div>
            <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
              <Youtube className="w-4 h-4 inline mr-1" />
              YouTube URL
            </label>
            <p className="text-xs text-[var(--color-text-muted)] mb-3">
              Paste a YouTube video URL. Supports youtube.com/watch, youtu.be, and youtube.com/shorts links.
            </p>

            <input
              type="text"
              value={url}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm font-regular ${
                url.length === 0
                  ? 'border-[var(--color-border)] focus:ring-[var(--color-primary)]'
                  : isValid
                  ? 'border-green-400 focus:ring-green-500'
                  : 'border-red-400 focus:ring-red-500'
              }`}
            />

            {url.length > 0 && (
              <div className={`mt-2 flex items-center gap-2 text-sm ${isValid ? 'text-green-700' : 'text-red-500'}`}>
                {isValid ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Valid YouTube URL detected</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span>Invalid URL — must be a YouTube video link</span>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {renderInstructionField()}

        {/* Live preview */}
        {isValid && embedUrl && (
          <div>
            <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">Preview</label>
            <div
              className="relative w-full rounded-lg overflow-hidden border border-[var(--color-border)] pb-[56.25%]"
              onContextMenu={(e) => e.preventDefault()}
            >
              <iframe
                src={embedUrl}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                title="YouTube Video Preview"
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderGenericEditor = () => {
    const data = localActivity?.activity_data as any || {}

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Question/Prompt
          </label>
          <textarea
            value={data.question || ''}
            onChange={(e) => handleActivityDataChange('question', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none font-regular"
            placeholder="Enter the main question or prompt"
          />
        </div>

        {renderInstructionField()}
        {renderTimeLimitSetting()}
      </div>
    )
  }

  const SHELL_COMMANDS = [
    'whois', 'host', 'dig', 'nslookup', 'ping', 'traceroute', 'tracepath',
    'curl', 'wget', 'ip', 'ifconfig', 'netstat', 'ss', 'openssl', 'nmap',
    'arp', 'mtr', 'nc', 'telnet', 'ftp', 'ssh-keyscan', 'getent', 'resolvectl'
  ]

  const renderShellEditor = () => {
    const data = localActivity?.activity_data as any || {}
    const allowedCommands: string[] = data.allowed_commands || []

    const toggleCommand = (cmd: string) => {
      if (allowedCommands.length === 0) {
        // All currently allowed — unchecking one starts an explicit restriction list
        handleActivityDataChange('allowed_commands', SHELL_COMMANDS.filter(c => c !== cmd))
      } else if (allowedCommands.includes(cmd)) {
        handleActivityDataChange('allowed_commands', allowedCommands.filter((c: string) => c !== cmd))
      } else {
        handleActivityDataChange('allowed_commands', [...allowedCommands, cmd])
      }
    }

    return (
      <div className="space-y-6">
        {!readOnly && <QuestionBankPicker activityType="command_shell" onSelect={loadFromQuestionBank} onBulkAdd={onBulkAddQuestions} />}

        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Question
          </label>
          <textarea
            value={data.question || ''}
            onChange={(e) => handleActivityDataChange('question', e.target.value)}
            rows={3}
            placeholder="Describe what the player needs to investigate…"
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Target (domain / IP)
          </label>
          <input
            type="text"
            value={data.target || ''}
            onChange={(e) => handleActivityDataChange('target', e.target.value)}
            placeholder="e.g. example.com or 8.8.8.8"
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] font-mono text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Expected Answer
          </label>
          <input
            type="text"
            value={data.correct_answer || ''}
            onChange={(e) => handleActivityDataChange('correct_answer', e.target.value)}
            placeholder="The correct answer players must submit"
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] text-sm"
          />
          <div className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="case_sensitive"
              checked={!!data.case_sensitive}
              onChange={(e) => handleActivityDataChange('case_sensitive', e.target.checked)}
              className="rounded"
            />
            <label htmlFor="case_sensitive" className="text-sm text-[var(--color-text-muted)]">
              Case-sensitive matching
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Correct Command
          </label>
          <input
            type="text"
            value={data.command || ''}
            onChange={(e) => handleActivityDataChange('command', e.target.value)}
            placeholder="e.g. dig example.com A +short"
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] font-mono text-sm"
          />
          <p className="text-xs text-[var(--color-text-muted)] mt-1">
            The command that produces the expected answer. Shown as reference only — not displayed to students.
          </p>
        </div>

        {renderHintsSection()}

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-[var(--color-text)]">
              Allowed Commands
            </label>
            <button
              type="button"
              onClick={() => handleActivityDataChange('allowed_commands', [])}
              className={`text-xs ${ac.text} hover:underline`}
            >
              Reset (allow all)
            </button>
          </div>
          <p className="text-xs text-[var(--color-text-muted)] mb-3">
            {allowedCommands.length === 0
              ? 'All whitelisted commands are allowed. Check boxes to restrict.'
              : `Restricted to: ${allowedCommands.join(', ')}`}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {SHELL_COMMANDS.map((cmd) => (
              <label
                key={cmd}
                className="flex items-center gap-2 p-2 rounded border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] cursor-pointer text-xs"
              >
                <input
                  type="checkbox"
                  checked={allowedCommands.length === 0 || allowedCommands.includes(cmd)}
                  onChange={() => toggleCommand(cmd)}
                  className="rounded"
                />
                <span className="font-mono text-[var(--color-text)]">{cmd}</span>
              </label>
            ))}
          </div>
        </div>

        {renderInstructionField()}
      </div>
    )
  }

  const renderPhishingQuizEditor = () => {
    const data = localActivity?.activity_data as any || {}
    const PHISHING_OPTIONS = ['Phishing', 'Legitimate']

    return (
      <div className="space-y-6">
        {!readOnly && <QuestionBankPicker activityType="phishing_quiz" onSelect={loadFromQuestionBank} onBulkAdd={onBulkAddQuestions} />}

        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Question
          </label>
          <textarea
            value={data.question || data.subject || ''}
            onChange={(e) => {
              handleActivityDataChanges({ question: e.target.value, subject: e.target.value })
            }}
            rows={2}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none font-regular"
            placeholder="Enter the email subject or question"
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-[var(--color-text)] mb-2 block">
            Answer Options
          </label>
          <div className="space-y-2">
            {PHISHING_OPTIONS.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="phishing_correct_answer"
                  checked={resolvePhishingAnswer(data) === (index === 0 ? 'phishing' : 'legitimate')}
                  onChange={() => {
                    handleActivityDataChanges({
                      correct_answer: index,
                      type: index === 0 ? 'phishing' : 'legitimate',
                      options: PHISHING_OPTIONS,
                    })
                  }}
                  disabled={readOnly}
                  className="w-4 h-4 text-[var(--color-primary)]"
                />
                <span className={`px-3 py-2 flex-1 border rounded-lg font-regular ${
                  index === 0
                    ? 'border-red-500/30 bg-red-500/5 text-red-500'
                    : 'border-green-500/30 bg-green-500/5 text-green-500'
                }`}>
                  {option}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">
            Select the correct answer for this email.
          </p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Explanation (optional)
          </label>
          <textarea
            value={data.explanation || ''}
            onChange={(e) => handleActivityDataChange('explanation', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none font-regular"
            placeholder="Explain why this email is phishing or legitimate (shown after answering)"
          />
        </div>

        {renderExplanationImageUpload(data)}

        {renderHintsSection()}
        {renderInstructionField()}
        {renderTimeLimitSetting()}
      </div>
    )
  }

  const renderPacketAnalysisEditor = () => {
    const data = localActivity?.activity_data as any || {}

    return (
      <div className="space-y-6">
        {!readOnly && <QuestionBankPicker activityType="packet_analysis" onSelect={loadFromQuestionBank} onBulkAdd={onBulkAddQuestions} />}

        {data.question_id ? (
          <div className="p-4 bg-[var(--color-surface-deep)] border border-[var(--color-border)] rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--color-text-muted)]">{data.category}</span>
              <span className="text-xs text-[var(--color-muted)] capitalize">{data.difficulty}</span>
            </div>
            <p className="text-sm font-semibold text-[var(--color-text)]">{data.question}</p>
            <p className="text-xs text-[var(--color-text-muted)]">{data.packets?.length || 0} packets · {data.options?.length || 0} options</p>
          </div>
        ) : (
          <p className="text-sm text-[var(--color-muted)] text-center py-4">Select a question from the bank above</p>
        )}

        {renderHintsSection()}
        {renderInstructionField()}
      </div>
    )
  }

  const renderCyberHygieneQuizEditor = () => {
    const data = localActivity?.activity_data as any || {}

    const rawOptions: string[] = data.options || []
    const options = [...rawOptions, '', '', '', ''].slice(0, 4) as string[]

    const updateOption = (index: number, value: string) => {
      const newOptions = [...options]
      newOptions[index] = value
      handleActivityDataChange('options', newOptions)
    }

    return (
      <div className="space-y-6">
        {!readOnly && <QuestionBankPicker activityType="cyber_hygiene_quiz" onSelect={loadFromQuestionBank} onBulkAdd={onBulkAddQuestions} />}

        {(data.question_id || data.question) ? (
          <>
            {data.category && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--color-text-muted)]">{data.category}</span>
                <span className="text-xs text-[var(--color-muted)] capitalize">{data.difficulty}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
                Question
              </label>
              <textarea
                value={data.question || ''}
                onChange={(e) => handleActivityDataChange('question', e.target.value)}
                readOnly={readOnly}
                rows={2}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none font-regular"
                placeholder="Enter your question here"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-[var(--color-text)] mb-2 block">
                Answer Options
              </label>
              <div className="space-y-2">
                {options.map((option: string, index: number) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="correct_answer_ch"
                      checked={data.correct_answer === index}
                      onChange={() => handleActivityDataChange('correct_answer', index)}
                      disabled={readOnly}
                      className="w-4 h-4 text-[var(--color-primary)]"
                    />
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      data.correct_answer === index
                        ? `${ac.bgSolid} text-white`
                        : 'bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]'
                    }`}>
                      {'ABCD'[index]}
                    </span>
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      readOnly={readOnly}
                      className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] font-regular"
                      placeholder={`Option ${'ABCD'[index]}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {data.explanation && (
              <div>
                <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
                  Explanation
                </label>
                <textarea
                  value={data.explanation || ''}
                  onChange={(e) => handleActivityDataChange('explanation', e.target.value)}
                  readOnly={readOnly}
                  rows={2}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none font-regular"
                />
              </div>
            )}
            {(data.explanation || data.explanation_image_url) && renderExplanationImageUpload(data)}
          </>
        ) : (
          <p className="text-sm text-[var(--color-muted)] text-center py-4">Select a question from the bank above</p>
        )}

        {renderHintsSection()}
        {renderInstructionField()}
        {renderTimeLimitSetting()}
      </div>
    )
  }

  const renderCodingQuizEditor = () => {
    const data = localActivity?.activity_data as any || {}

    return (
      <div className="space-y-6">
        {!readOnly && <QuestionBankPicker activityType="coding_quiz" onSelect={loadFromQuestionBank} onBulkAdd={onBulkAddQuestions} />}

        {data.question_id ? (
          <div className="p-4 bg-[var(--color-surface-deep)] border border-[var(--color-border)] rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--color-text-muted)]">{data.category}</span>
              <span className="text-xs text-[var(--color-muted)] capitalize">{data.difficulty}</span>
            </div>
            <p className="text-sm font-semibold text-[var(--color-text)]">{data.title}</p>
            <p className="text-xs text-[var(--color-text-muted)]">{data.question}</p>
            <p className="text-xs text-[var(--color-text-muted)]">{data.options?.length || 0} options</p>
          </div>
        ) : (
          <p className="text-sm text-[var(--color-muted)] text-center py-4">Select a question from the bank above</p>
        )}

        {renderHintsSection()}
        {renderInstructionField()}
      </div>
    )
  }

  const renderPythonReplEditor = () => {
    const data = localActivity?.activity_data as any || {}
    const testCases: { id: string; description: string; expected_output: string }[] = data.test_cases || []
    const hints: string[] = data.hints || []

    const addTestCase = () => {
      handleActivityDataChange('test_cases', [
        ...testCases,
        { id: `test_${Date.now()}`, description: '', expected_output: '' },
      ])
    }

    const updateTestCase = (idx: number, field: string, value: string) => {
      handleActivityDataChange('test_cases', testCases.map((tc, i) => i === idx ? { ...tc, [field]: value } : tc))
    }

    const removeTestCase = (idx: number) => {
      handleActivityDataChange('test_cases', testCases.filter((_, i) => i !== idx))
    }

    const addHint = () => {
      handleActivityDataChange('hints', [...hints, ''])
    }

    const updateHint = (idx: number, value: string) => {
      handleActivityDataChange('hints', hints.map((h, i) => i === idx ? value : h))
    }

    const removeHint = (idx: number) => {
      handleActivityDataChange('hints', hints.filter((_, i) => i !== idx))
    }

    return (
      <div className="space-y-6">
        {/* Question Bank */}
        {!readOnly && <QuestionBankPicker activityType="python_repl" onSelect={loadFromQuestionBank} onBulkAdd={onBulkAddQuestions} />}

        {/* Question */}
        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Question
          </label>
          <textarea
            value={data.question || ''}
            onChange={(e) => handleActivityDataChange('question', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none font-regular"
            placeholder='e.g. Write a function that returns the sum of two numbers'
          />
        </div>

        {/* Starter Code */}
        <div>
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Starter Code
          </label>
          <textarea
            value={data.starter_code || ''}
            onChange={(e) => handleActivityDataChange('starter_code', e.target.value)}
            rows={6}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] text-sm font-mono bg-gray-900 text-green-400 resize-none"
            placeholder="# Write starter code here..."
          />
        </div>

        {/* Test Cases */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-[var(--color-text)]">Test Cases</label>
            <button
              type="button"
              onClick={addTestCase}
              className={`text-xs ${ac.text} ${ac.textHover} font-medium`}
            >
              + Add Test Case
            </button>
          </div>
          {testCases.length === 0 && (
            <p className="text-xs text-[var(--color-text-muted)]">No test cases yet. Add one to give students automatic feedback.</p>
          )}
          <div className="space-y-3">
            {testCases.map((tc, idx) => (
              <div key={tc.id} className="border border-[var(--color-border)] rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-[var(--color-text-muted)]">Test {idx + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeTestCase(idx)}
                    className="text-xs text-red-500 hover:text-red-500"
                  >
                    Remove
                  </button>
                </div>
                <input
                  type="text"
                  value={tc.description}
                  onChange={(e) => updateTestCase(idx, 'description', e.target.value)}
                  placeholder="Description (e.g. Basic test)"
                  className="w-full px-2 py-1.5 text-sm border border-[var(--color-border)] rounded focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                />
                <input
                  type="text"
                  value={tc.expected_output}
                  onChange={(e) => updateTestCase(idx, 'expected_output', e.target.value)}
                  placeholder="Expected output (e.g. 5)"
                  className="w-full px-2 py-1.5 text-sm border border-[var(--color-border)] rounded font-mono focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Hints */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-[var(--color-text)]">Hints</label>
            <button
              type="button"
              onClick={addHint}
              className={`text-xs ${ac.text} ${ac.textHover} font-medium`}
            >
              + Add Hint
            </button>
          </div>
          <div className="space-y-2">
            {hints.map((h, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input
                  type="text"
                  value={h}
                  onChange={(e) => updateHint(idx, e.target.value)}
                  placeholder={`Hint ${idx + 1}`}
                  className="flex-1 px-3 py-1.5 text-sm border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                />
                <button
                  type="button"
                  onClick={() => removeHint(idx)}
                  className="text-xs text-red-500 hover:text-red-500 shrink-0"
                >
                  ×
                </button>
              </div>
            ))}
            {hints.length === 0 && (
              <p className="text-xs text-[var(--color-text-muted)]">No hints. Students will work without guidance.</p>
            )}
          </div>
        </div>

        {renderInstructionField()}
        {renderTimeLimitSetting()}
      </div>
    )
  }

  const renderActivitySpecificEditor = () => {
    switch (localActivity?.activity_type) {
      case 'mcq':
        return renderMCQEditor()
      case 'coding':
        return renderCodingEditor()
      case 'potato_pirates':
        return renderPotatoPiratesEditor()
      case 'pdf_slideshow':
        return renderPdfSlideshowEditor()
      case 'google_slides':
        return renderGoogleSlidesEditor()
      case 'youtube_video':
        return renderYouTubeVideoEditor()
      case 'command_shell':
        return renderShellEditor()
      case 'phishing_quiz':
        return renderPhishingQuizEditor()
      case 'packet_analysis':
        return renderPacketAnalysisEditor()
      case 'cyber_hygiene_quiz':
        return renderCyberHygieneQuizEditor()
      case 'coding_quiz':
        return renderCodingQuizEditor()
      case 'python_repl':
        return renderPythonReplEditor()
      case 'breach_check':
      case 'password_check':
        return renderHibpEditor()
      default:
        return renderGenericEditor()
    }
  }

  const renderHibpEditor = () => {
    return (
      <div className="space-y-6">
        {renderInstructionField()}
        {renderTimeLimitSetting()}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Editor Header */}
      <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="ds-section-title">{readOnly ? 'Activity Details' : 'Edit Activity'}</h2>
          </div>

          {!readOnly && (
            <div className="flex items-center space-x-2">
              {hasChanges && (
                <button
                  onClick={handleReset}
                  className="p-2 text-[var(--color-muted)] hover:text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface-hover)]"
                  title="Reset changes"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Editor Content */}
      <div className={`flex-1 overflow-y-auto p-4 space-y-6 activity-editor-inputs${readOnly ? ' opacity-80 activity-editor-readonly' : ''}`}>
        <div className="mb-4">
          <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
            Activity Title
          </label>
          <input
            type="text"
            value={localActivity?.title || ''}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            readOnly={readOnly}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] font-regular"
            placeholder="Enter activity title"
          />
        </div>
        {renderActivitySpecificEditor()}

      </div>
    </div>
  )
}